import React from 'react';
import { Toaster, toast } from '../ui/simple-toaster';
import {
  LogoutConfirmModal,
  NightRestConfirmModal,
  JourneyPlanningModal,
  RadiusWarningModal,
  RadiusAcknowledgedModal,
  FinishDayModal,
  ServiceModal,
  ExportPDFModal,
  BaseAddressModal,
  AcknowledgementModal,
  VehicleSelectionModal,
  OtherVehicleFormModal,
  PrimaryProductionRadiusModal
} from './DriverModals';
import OdometerConfirmModal from './OdometerConfirmModal';
import FuelStopModal from './FuelStopModal';
import MyVehicleModal from './MyVehicleModal';
import MyDiaryModal from './MyDiaryModal';
import ReportsModal from './ReportsModal';
import RestLocationModal from './RestLocationModal';
import FleetInvitesModal from './FleetInvitesModal';

/**
 * Renders every modal used by the driver app.
 * All visibility flags and handlers are passed in via props.
 */
export const DriverModalLayer = ({
  // Modal visibility flags
  showLogoutConfirm, showNightRestConfirm, showVehicleModal, showOtherVehicleForm,
  showRadiusSelectionModal, showJourneyModal, showRadiusWarning, showRadiusAcknowledged,
  showFinishModal, showAboutModal, showExportModal, showBaseAddressModal,
  showAcknowledgementModal, showOdometerConfirm, showFuelStopModal,
  showMyVehicleModal, showMyDiaryModal, showReportsModal, showRestLocationModal,
  showFleetInvitesModal,
  // Modal close setters
  setShowVehicleModal, setShowOtherVehicleForm, setShowJourneyModal,
  setShowRadiusWarning, setShowRadiusAcknowledged, setShowFinishModal,
  setShowAboutModal, setShowExportModal, setShowBaseAddressModal,
  setShowFuelStopModal, setShowMyVehicleModal, setShowMyDiaryModal,
  setShowReportsModal, setShowFleetInvitesModal,
  // Logout
  confirmLogout, cancelLogoutAndSwitchToRest,
  // Night rest
  nightRestMessage, nightRestConfirmType,
  handleNightRestConfirmProceed, setShowNightRestConfirm,
  // Vehicle
  vehicles, vehicleInfo, vehicleLoading, handleVehicleSelect,
  otherVehicleForm, setOtherVehicleForm, handleOtherVehicleSubmit,
  // Radius
  pendingVehicleSelection, handleRadiusSelection, cancelRadiusSelection, radiusSelectionLoading,
  // Journey
  journeyDestination, handleAddressChange, checkJourneyRadius, journeyLoading,
  addressSuggestions, showSuggestions, selectAddress,
  setJourneyDestination, journeyResult, setJourneyResult,
  // Finish Day
  handleFinishDay, driverState,
  // Export
  handleExportPDF, handleSharePDF, exportLoading,
  // Base Address
  baseAddressInput, setBaseAddressInput, handleBaseAddressUpdate,
  // Acknowledgement
  ackCheckbox, setAckCheckbox, setAcknowledgementAccepted, setShowAcknowledgementModal,
  // Odometer
  handleOdometerConfirm, odometerLoading, setPendingWorkStart,
  // Fuel Stop
  handleFuelStop,
  // My Vehicle
  loadVehicles,
  // My Diary / Reports
  driverInfo, user,
  // Rest Location
  handleRestLocationConfirm, handleRestLocationClose,
}) => (
  <>
    {showLogoutConfirm && (
      <LogoutConfirmModal onConfirm={confirmLogout} onCancel={cancelLogoutAndSwitchToRest} />
    )}

    {showNightRestConfirm && (
      <NightRestConfirmModal
        message={nightRestMessage}
        confirmType={nightRestConfirmType}
        onProceed={handleNightRestConfirmProceed}
        onCancel={() => setShowNightRestConfirm(false)}
      />
    )}

    {showVehicleModal && (
      <VehicleSelectionModal
        vehicles={vehicles}
        selectedVehicleId={vehicleInfo?.vehicle_id}
        loading={vehicleLoading}
        onSelect={handleVehicleSelect}
        onAddOther={() => setShowOtherVehicleForm(true)}
        onClose={() => setShowVehicleModal(false)}
      />
    )}

    {showOtherVehicleForm && (
      <OtherVehicleFormModal
        form={otherVehicleForm}
        onChange={setOtherVehicleForm}
        loading={vehicleLoading}
        onSubmit={handleOtherVehicleSubmit}
        onClose={() => setShowOtherVehicleForm(false)}
      />
    )}

    {showRadiusSelectionModal && pendingVehicleSelection && (
      <PrimaryProductionRadiusModal
        vehicle={pendingVehicleSelection.vehicle}
        onSelectRadius={handleRadiusSelection}
        onCancel={cancelRadiusSelection}
        loading={radiusSelectionLoading}
      />
    )}

    {showJourneyModal && (
      <JourneyPlanningModal
        destination={journeyDestination}
        onDestinationChange={handleAddressChange}
        onClose={() => { setShowJourneyModal(false); setJourneyDestination(''); }}
        onCheck={checkJourneyRadius}
        loading={journeyLoading}
        suggestions={addressSuggestions}
        showSuggestions={showSuggestions}
        onSelectAddress={selectAddress}
      />
    )}

    {showRadiusWarning && (
      <RadiusWarningModal
        distanceKm={journeyResult?.distance_km}
        onConfirm={() => { setShowRadiusWarning(false); setShowRadiusAcknowledged(true); }}
        onCancel={() => { setShowRadiusWarning(false); setJourneyResult(null); }}
      />
    )}

    {showRadiusAcknowledged && (
      <RadiusAcknowledgedModal
        onClose={() => { setShowRadiusAcknowledged(false); setJourneyDestination(''); setJourneyResult(null); }}
      />
    )}

    {showFinishModal && (
      <FinishDayModal
        onFinish={handleFinishDay}
        onClose={() => setShowFinishModal(false)}
        restTracking={driverState?.rest_tracking}
        regime={driverState?.regime}
      />
    )}

    {showAboutModal && (
      <ServiceModal onClose={() => setShowAboutModal(false)} />
    )}

    {showExportModal && (
      <ExportPDFModal
        onExport={handleExportPDF}
        onShare={handleSharePDF}
        loading={exportLoading}
        onClose={() => setShowExportModal(false)}
      />
    )}

    {showBaseAddressModal && (
      <BaseAddressModal
        value={baseAddressInput}
        onChange={(e) => setBaseAddressInput(e.target.value)}
        timezone={driverState?.base_timezone}
        onSave={() => handleBaseAddressUpdate(baseAddressInput)}
        onClose={() => setShowBaseAddressModal(false)}
      />
    )}

    {showAcknowledgementModal && (
      <AcknowledgementModal
        checked={ackCheckbox}
        onCheckChange={(e) => setAckCheckbox(e.target.checked)}
        onSubmit={() => {
          if (ackCheckbox) {
            localStorage.setItem('lawd_driver_acknowledged', 'true');
            setAcknowledgementAccepted(true);
            setShowAcknowledgementModal(false);
            toast.success('Acknowledgement accepted');
          }
        }}
      />
    )}

    {showOdometerConfirm && vehicleInfo && (
      <OdometerConfirmModal
        isOpen={showOdometerConfirm}
        onClose={() => {
          setShowOdometerConfirm(false);
          setPendingWorkStart(false);
        }}
        onConfirm={handleOdometerConfirm}
        vehicle={vehicleInfo}
        source="WORK_START"
        loading={odometerLoading}
      />
    )}

    {showFuelStopModal && vehicleInfo && (
      <FuelStopModal
        isOpen={showFuelStopModal}
        onClose={() => setShowFuelStopModal(false)}
        onConfirm={handleFuelStop}
        vehicle={vehicleInfo}
        loading={odometerLoading}
      />
    )}

    {showMyVehicleModal && (
      <MyVehicleModal
        isOpen={showMyVehicleModal}
        onClose={() => setShowMyVehicleModal(false)}
        vehicle={vehicleInfo}
        onVehicleUpdated={() => {
          loadVehicles();
          setShowMyVehicleModal(false);
        }}
      />
    )}

    {showMyDiaryModal && driverInfo && (
      <MyDiaryModal
        isOpen={showMyDiaryModal}
        onClose={() => setShowMyDiaryModal(false)}
        driverId={driverInfo.driver_id}
        driverName={`${user?.first_name || ''} ${user?.last_name || ''}`}
      />
    )}

    {showReportsModal && driverInfo && (
      <ReportsModal
        isOpen={showReportsModal}
        onClose={() => setShowReportsModal(false)}
        driverId={driverInfo.driver_id}
        driverName={`${user?.first_name || ''} ${user?.last_name || ''}`}
      />
    )}

    {showRestLocationModal && driverInfo && (
      <RestLocationModal
        isOpen={showRestLocationModal}
        onClose={handleRestLocationClose}
        onConfirm={handleRestLocationConfirm}
        driverId={driverInfo.driver_id}
      />
    )}

    <Toaster />
  </>
);
